<template>
    <aside class="editor" :class="{ open: !!selectedBlock }">
      <div class="editor-head">
        <div class="eh-title">文字內容</div>
        <button class="x" type="button" @click="$emit('close')">×</button>
      </div>
  
      <div v-if="!selectedBlock" class="editor-empty">
        點選左側頁面上的任一文字框即可編輯
      </div>
  
      <div v-else class="editor-body">
        <div class="field">
          <label>文字內容</label>
          <textarea
            v-model="selectedBlock.text"
            class="ta"
            :class="{ oneline: isOneLineSelected }"
            :wrap="isOneLineSelected ? 'off' : 'soft'"
            rows="1"
          />
        </div>
  
        <div class="row">
          <div class="field">
            <label>字型大小</label>
            <div class="slider-row">
              <input type="range" min="50" max="200" step="1" v-model.number="selectedBlock.fontScale" />
              <div class="pct">{{ selectedBlock.fontScale }}%</div>
            </div>
          </div>
        </div>
  
        <div class="row">
          <div class="field">
            <label>文字顏色</label>
            <div class="color-row">
              <input
                :key="selectedBlock.id"
                type="color"
                :value="selectedColor"
                @input="$emit('colorPick', $event)"
                @change="$emit('colorPick', $event)"
              />
              <input
                class="hex"
                type="text"
                :value="hexDraft"
                @input="onHexInput"
                @blur="$emit('commitHex')"
                @keydown.enter.prevent="$emit('commitHex')"
              />
            </div>
          </div>
        </div>
  
        <div class="actions">
          <button class="btn ghost" type="button" @click="$emit('reset')">重置此框</button>
          <button class="btn primary" type="button" @click="$emit('apply')">套用編輯</button>
        </div>
      </div>
    </aside>
  </template>
  
  <script setup lang="ts">
  import type { OcrBlock } from '@/types/ainotebook'
  
  const props = defineProps<{
    selectedBlock: OcrBlock | null
    selectedColor: string
    hexDraft: string
    isOneLineSelected: boolean
  }>()
  
  const emit = defineEmits<{
    (e: 'close'): void
    (e: 'colorPick', ev: Event): void
    (e: 'commitHex'): void
    (e: 'reset'): void
    (e: 'apply'): void
  }>()
  
  function onHexInput(e: Event) {
    const v = (e.target as HTMLInputElement).value
    // parent keeps draft in sync via composable; simplest is dispatch a synthetic event:
    ;(e.target as HTMLInputElement).value = v
    // we rely on v-model in parent composable, so no-op here if you prefer.
    // If你要完全雙向，建議把 hexDraft 改成 v-model:hexDraft（這版先保持簡單）
  }
  </script>
  
  <style scoped lang="scss">
  .editor {
    min-height: 0;
    height: 100%;
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 16px;
    background: rgba(10,18,33,0.55);
    backdrop-filter: blur(10px);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  
  .editor-head {
    padding: 12px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid rgba(255,255,255,0.06);
  
    .eh-title { font-weight: 800; }
    .x {
      width: 32px; height: 32px;
      border-radius: 10px;
      border: 1px solid rgba(255,255,255,0.08);
      background: rgba(255,255,255,0.04);
      color: #e8eefc;
      cursor: pointer;
    }
  }
  
  .editor-empty {
    flex: 1;
    display: grid;
    place-items: center;
    padding: 20px;
    color: rgba(232,238,252,0.65);
    text-align: center;
  }
  
  .editor-body {
    flex: 1;
    min-height: 0;
    overflow: auto;
    padding: 12px;
    display: grid;
    gap: 12px;
  
    scrollbar-width: thin;
    scrollbar-color: rgba(255,255,255,0.12) transparent;
    &::-webkit-scrollbar { width: 8px; }
    &::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.12); border-radius: 999px; }
  
    .field {
      display: grid;
      gap: 8px;
      label { font-size: 12px; color: rgba(232,238,252,0.65); }
  
      textarea.ta, input[type="text"] {
        border-radius: 14px;
        border: 1px solid rgba(255,255,255,0.10);
        background: rgba(255,255,255,0.04);
        color: #e8eefc;
        padding: 10px 12px;
        outline: none;
        font-size: 14px;
        line-height: 1.2;
        resize: none;
        min-height: 40px;
        max-height: 180px;
        overflow-y: hidden;
        white-space: pre-wrap;
        word-break: break-word;
      }
  
      textarea.ta.oneline {
        white-space: nowrap;
        overflow-x: auto;
        overflow-y: hidden;
        height: 40px !important;
      }
    }
  
    .slider-row {
      display: grid;
      grid-template-columns: 1fr 56px;
      gap: 10px;
      align-items: center;
      .pct { text-align: right; font-weight: 800; }
    }
  
    .color-row {
      display: grid;
      grid-template-columns: 44px 1fr;
      gap: 10px;
      align-items: center;
  
      input[type="color"] {
        width: 44px;
        height: 40px;
        padding: 0;
        border: none;
        background: transparent;
      }
      .hex { height: 40px; border-radius: 12px; }
    }
  
    .actions {
      display: flex;
      gap: 10px;
      justify-content: flex-end;
      padding-top: 6px;
    }
  }
  
  .btn {
    height: 40px;
    padding: 0 14px;
    border-radius: 999px;
    border: 1px solid rgba(255,255,255,0.10);
    background: rgba(255,255,255,0.05);
    color: #e8eefc;
    cursor: pointer;
    font-weight: 700;
  }
  .btn.primary {
    background: linear-gradient(180deg, rgba(29,123,255,1), rgba(29,123,255,0.85));
    border-color: rgba(29,123,255,0.55);
  }
  .btn.ghost { background: rgba(255,255,255,0.03); }
  
  @media (max-width: 1100px) {
    .editor { display: none; }
  }
  </style>
  